import React, { useEffect, useState } from 'react';
import { LinearProgress, makeStyles } from '@material-ui/core';
import { CustomizedButton } from '@components/Customized';
import { clsx, colorSet } from '@components/Provider';
import {
  DesignRequestStageEnum,
  PcbRequestStageEnum,
  RequestQueryString
} from '../types';
import { debounce } from 'lodash';
import useQueryString from '@hooks/useQueryString';
import { snackbarBottom } from '@vars';

interface RequestBottomNavProps {
  stage: PcbRequestStageEnum | DesignRequestStageEnum;
  steps: PcbRequestStageEnum[] | DesignRequestStageEnum[];
  onClickNext: () => void;
  onClickBack: () => void;
  setButtonStateHandler: React.Dispatch<
    React.SetStateAction<{
      disableHandler: (value: boolean) => void;
    }>
  >;
}

const RequestBottomNav: React.FC<RequestBottomNavProps> = ({
  stage,
  steps,
  setButtonStateHandler,
  onClickNext,
  onClickBack
}) => {
  const { query } = useQueryString<RequestQueryString>();
  const prevDisabled =
    query.stage === DesignRequestStageEnum.PROCESS_SELECT &&
    query.reWrite === 'true';

  const [disabledNextButton, setDisabledNextButton] = useState<boolean>(true);
  const { bottomCta, buttonGroup, buttonDisabled } = usePCBRequestStyles();

  useEffect(() => {
    snackbarBottom(83);

    return () => {
      snackbarBottom();
    };
  }, []);

  useEffect(() => {
    setButtonStateHandler({
      disableHandler: (value) => {
        setDisabledNextButton(value);
      }
    });
  }, [setButtonStateHandler]);

  return (
    <div className={bottomCta}>
      <LinearProgress
        className="progress"
        variant="determinate"
        value={
          (steps.findIndex(
            (step: DesignRequestStageEnum | PcbRequestStageEnum) =>
              step === stage
          ) /
            (steps.length - 1)) *
          100
        }
      />
      <div
        className={clsx(buttonGroup, {
          [buttonDisabled]: prevDisabled
        })}
      >
        <CustomizedButton
          className="prevButton"
          variant="contained"
          color="default"
          size="large"
          onClick={debounce((e) => {
            e.preventDefault();
            onClickBack();
          }, 50)}
        >
          뒤로가기
        </CustomizedButton>

        <CustomizedButton
          data-testid="next-button"
          className={clsx('nextButton clickable')}
          variant="contained"
          size="large"
          color="primary"
          disabled={disabledNextButton}
          onClick={debounce((e) => {
            e.preventDefault();
            onClickNext();
          }, 50)}
        >
          <i
            className={
              stage !== PcbRequestStageEnum.PREVIEW
                ? 'request-gtm-processType-btn'
                : 'request-gtm-sub-submitComplete-btn'
            }
          >
            {stage !== PcbRequestStageEnum.PREVIEW
              ? '다음단계'
              : '견적 요청하기'}
          </i>
        </CustomizedButton>
      </div>
    </div>
  );
};

export default RequestBottomNav;

const usePCBRequestStyles = makeStyles((theme) => ({
  bottomCta: {
    position: 'sticky',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colorSet.gray000,
    width: '100%',
    minHeight: 83,
    padding: '0px 24px',
    '& .progress': {
      height: '2px',
      backgroundColor: colorSet.gray200,
      marginBottom: 16,
      width: 'calc(100% + 48px)',
      transform: 'translateX(-24px)',
      [theme.breakpoints.down('sm')]: {
        width: '100vw'
      },
      '& .MuiLinearProgress-bar': {
        backgroundColor: colorSet.gray900,
        maxWidth: '100%'
      }
    }
  },
  buttonGroup: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    '& .nextButton': {
      backgroundColor: colorSet.primary500,
      '&:disabled': {
        backgroundColor: colorSet.gray200,
        '& > span': {
          color: colorSet.gray500
        }
      }
    },
    [theme.breakpoints.down('sm')]: {
      gap: '8px',
      '& button': {
        flex: 1
      }
    }
  },
  buttonDisabled: {
    justifyContent: 'right',
    '& .prevButton': {
      display: 'none'
    }
  }
}));
